import { useState } from "react";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { Box } from "@mui/material";
import Text from "@CubeComponents/html/Text/Text";
import IconComponent from "@CubeComponents/icons";
import { useTranslation } from "@i18n";

/**
 * Paper sizes supported by PDF export (puppeteer PaperFormat names). Single source for every
 * module's export options; the worker maps the value to page format and viewport width.
 */
export const PAPER_SIZES = ["Letter", "Legal", "Tabloid", "Ledger", "A0", "A1", "A2", "A3", "A4"] as const;
export type PaperSize = (typeof PAPER_SIZES)[number];

export const DEFAULT_PAPER_SIZE: PaperSize = "A4";

interface PaperSizeSelectorProps {
  value: string;
  onChange: (size: PaperSize) => void;
}

/**
 * Labeled paper-size dropdown for PDF export dialogs. Extracted from the Forms export modal so
 * every module's export flow renders the same control.
 */
function PaperSizeSelector({ value, onChange }: PaperSizeSelectorProps) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleSelect = (size: PaperSize) => {
    onChange(size);
    setAnchorEl(null);
  };

  return (
    <div className="flex justify-between w-full">
      <span>
        <Text>{t("Paper Size")}</Text>
      </span>
      <Box
        className="flex items-center gap-1 cursor-pointer"
        data-testid="paperSizeSelector"
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        {value}
        <IconComponent name="arrowDownSm" width="1rem" height="1rem" />
      </Box>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        {PAPER_SIZES.map((size) => (
          <MenuItem
            key={size}
            onClick={() => handleSelect(size)}
            sx={{
              minWidth: "7.8125rem",
            }}
          >
            {size}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
}

export default PaperSizeSelector;
